import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Drawer, Skeleton, Typography } from 'antd';
import { ClusterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  EvaluationModelRole,
  formatRates,
  getModelPlan,
  setRoleModel,
  type EvaluationModelOption,
  type EvaluationModelPlanView,
  type EvaluationModelRoleView,
} from '@/api/scenarioIntelligence';
import { ModelPickerDrawer, ROLE_TONE, modelLine } from '@/components/ModelPickerDrawer';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// EvaluationModelsDrawer — the Scenario Intelligence model plan (plan §6, D-SI-9). Scenario, Critic and Evaluator are
// settings that name a deployment; changing one writes the next plan version on the server. The plan is platform-wide;
// each practice runs a role on its own registered row for that deployment, and the server says when it has none.
// What each role is used by today is stated plainly, so a setting that nothing reads yet never looks active.
// ─────────────────────────────────────────────────────────────────────────────

/** Who reads each role today. Kept beside the role so a new consumer updates the sentence in the same change. */
export const ROLE_USE: Record<EvaluationModelRole, string> = {
  [EvaluationModelRole.Scenario]: 'Used by “Generate scenarios” and by “Draft with AI” in the scenario editor. A generation or a draft can choose another deployment for itself.',
  [EvaluationModelRole.Critic]: 'Nothing uses the Critic yet; the coverage planner that will use it is not built.',
  [EvaluationModelRole.Evaluator]:
    'No semantic check runs yet: every check today is deterministic. The Evaluator judges once the semantic judge lands (D-SI-7).',
};

export const EvaluationModelsDrawer = observer(function EvaluationModelsDrawer({
  open,
  onClose,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  onChanged?: (plan: EvaluationModelPlanView) => void;
}) {
  const { themeStore, merchantContextStore: ctx } = useStore();
  const isDark = themeStore.mode === 'dark';
  const merchantId = ctx.selectedMerchantId;
  const practice = merchantId != null ? ctx.merchants.find((m) => m.id === merchantId) : undefined;
  const practiceLabel = practice ? `${practice.title} · #${practice.id}` : merchantId != null ? `practice #${merchantId}` : undefined;

  const [plan, setPlan] = useState<EvaluationModelPlanView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState<EvaluationModelRole | null>(null);
  const [picking, setPicking] = useState<EvaluationModelRoleView | null>(null);
  const request = useRef(0);

  useEffect(() => {
    if (!open) return;
    const mine = ++request.current;
    setPlan(null);
    setError(null);
    setSaveError(null);
    if (merchantId == null) {
      setError('Choose a practice first: a model role runs on the practice’s own registered deployments.');
      return;
    }
    getModelPlan(merchantId)
      .then((p) => mine === request.current && setPlan(p))
      .catch((e: Error) => mine === request.current && setError(e.message));
  }, [open, merchantId]);

  const choose = async (role: EvaluationModelRole, option: EvaluationModelOption | null) => {
    setSaving(role);
    setSaveError(null);
    try {
      const next = await setRoleModel(role, option?.aIModelId ?? null, merchantId);
      setPlan(next);
      onChanged?.(next);
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(null);
    }
  };

  const headerBg = isDark ? colors.slate[800] : '#FFFFFF';
  const bodyBg = isDark ? colors.slate[900] : colors.slate[50];
  const borderColor = isDark ? colors.slate[700] : colors.slate[200];
  const card = { background: headerBg, border: `1px solid ${borderColor}`, borderRadius: 10, padding: 16 };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="min(560px, 100vw)"
      closable={false}
      title={null}
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' }, header: { display: 'none' } }}
    >
      <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${borderColor}`, background: headerBg, display: 'flex', gap: 12 }}>
        <ClusterOutlined style={{ fontSize: 18, color: colors.ink[600], marginTop: 3 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 15, display: 'block' }}>Evaluation models</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Which deployment drafts, critiques and judges. Platform-wide: each practice runs a role on its own registered row
            for the deployment chosen here. The agent under test always runs on its own model.
          </Text>
        </div>
        <Button size="small" onClick={onClose}>Close</Button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', background: bodyBg, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {error && <Alert type="error" showIcon title="Could not read the evaluation model plan" description={error} />}
        {!plan && !error && <Skeleton active paragraph={{ rows: 8 }} />}
        {plan && plan.version === 0 && (
          <Alert
            type="warning"
            showIcon
            title="No evaluation model plan yet"
            description="The plan is created by scenario-intelligence sql/002 (Scenario and Evaluator start on gpt-6-luna). Until it runs, drafting with AI is refused rather than answered by another model."
          />
        )}
        {saveError && <Alert type="error" showIcon closable title="The choice was not saved" description={saveError} onClose={() => setSaveError(null)} />}

        {plan?.roles.map((r) => (
          <section key={r.role} style={card} aria-label={r.label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <StatusPill tone={ROLE_TONE[r.role]}>{r.label}</StatusPill>
              {r.qualification && <StatusPill tone={r.qualification === 'Qualifying' ? 'amber' : 'slate'}>{r.qualification}</StatusPill>}
              <Button
                size="small"
                style={{ marginLeft: 'auto' }}
                loading={saving === r.role}
                disabled={merchantId == null || plan.version === 0}
                onClick={() => setPicking(r)}
              >
                Change…
              </Button>
            </div>
            <RoleModel role={r} isDark={isDark} />
            {r.problem && <Alert style={{ marginTop: 8 }} type="error" showIcon title={r.problem} />}
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>{r.rule}</Text>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>{ROLE_USE[r.role]}</Text>
          </section>
        ))}

        {plan && plan.version > 0 && (
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            {`Plan version ${plan.version}`}
            {plan.changedAtUtc ? ` · changed ${dayjs(plan.changedAtUtc).format('D MMM YYYY HH:mm')}` : ''}
            {plan.note ? ` · ${plan.note}` : ''}
          </Text>
        )}
      </div>

      {picking && (
        <ModelPickerDrawer
          open={picking != null}
          onClose={() => setPicking(null)}
          role={picking.role}
          merchantId={merchantId}
          practiceLabel={practiceLabel}
          selectedId={picking.row?.aIModelId ?? null}
          allowOff={picking.mayBeOff}
          onSelect={(o) => void choose(picking.role, o)}
        />
      )}
    </Drawer>
  );
});

function RoleModel({ role, isDark }: { role: EvaluationModelRoleView; isDark: boolean }) {
  const textColor = isDark ? colors.slate[50] : colors.slate[900];
  if (!role.deployment) {
    return <Text style={{ fontSize: 13.5, color: textColor }}>{role.mayBeOff ? 'Off' : 'Not set'}</Text>;
  }
  const rates = role.row ? formatRates(role.row) : null;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
      <Text strong style={{ fontSize: 14, color: textColor }}>{modelLine(role.row) ?? role.deployment}</Text>
      {role.row &&
        (rates ? (
          <Text type="secondary" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{`${rates} per 1M tokens`}</Text>
        ) : (
          <StatusPill tone="slate">Price unavailable</StatusPill>
        ))}
    </div>
  );
}
